import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table, TableBody, TableCell, TableHead, TableRow, Paper,
  Box, Button, CircularProgress, Typography,
  IconButton, TextField, InputAdornment, TablePagination
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import EventIcon from "@mui/icons-material/Event";
import GroupIcon from "@mui/icons-material/Group";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import moment from "moment";
import { useTheme } from "@mui/material/styles";
import { ManagerStyle } from "./Manager.style";
import { CustomerStyle } from "../../insurance/Customers.styles";
import InformationDialog from "../../../base/dialog-body/Information.dialog";
import AppButton from "../../../base/AppButton.component";
import { TYPE } from "../../../../utils/Utils";

/* Default date range: first of the current month through today */
const getDefaultDateRange = () => ({
  startDate: moment().startOf("month").format("MM/DD/YYYY"),
  endDate: moment().format("MM/DD/YYYY"),
  f_startDate: moment().startOf("month").format("YYYY-MM-DD"),
  f_endDate: moment().format("YYYY-MM-DD"),
});

// Escape a value for CSV: wrap in quotes if it contains a comma, quote or newline
const escapeCsv = (value) => {
  const str = String(value ?? "");
  return /[",\r\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
};

// Shared inline "(+n)" badge, same visual language as the KPI cards
// on GroupDashboardComponent — small, bold, primary-green, appended right
// after the base number in a table cell.
const FollowUpBadge = ({ value, color }) => (
  <Box
    component="span"
    sx={{
      color: color,
      fontSize: "0.85em",
      fontWeight: 600,
      ml: 0.5,
    }}
  >
    (+{value ?? 0})
  </Box>
);

const DealershipTableComponent = ({
  filterData,
  fetchInput,
  setFetchInput,
  setFilterData,
  checkService,
  dealershipData,
  loading,
  dialog,
  switchDealership,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();

  // MUI TablePagination is 0-indexed
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchText, setSearchText] = useState("");

  const handleDealershipClick = async (dealership) => {
    // Find the dealership name from the clicked row
    const dealershipName = dealership.dealership_name;
    if (switchDealership && dealershipName && dialog) {
      await switchDealership(dealershipName);
      // Show refresh dialog
      dialog({
        open: true,
        bodyType: TYPE.DEFAULT,
        title: "Warning",
        body: (
          <InformationDialog
            text={
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <p>
                  You are switching dealerships. Please
                  click the REFRESH button below
                </p>
                <AppButton
                  sx={{ width: "40%", alignSelf: "center" }}
                  buttonText="Refresh"
                  size="small"
                  variant="contained"
                  clickHandler={() => {
                    // Set flag to show home component, then navigate and refresh
                    sessionStorage.setItem("navigatedToHome", "true");
                    window.location.href = "/";
                  }}
                />
              </div>
            }
          />
        ),
      });
    }
  };

  const handleReset = () => {
    const defaults = getDefaultDateRange();
    setFilterData({
      ...filterData,
      startDate: defaults.startDate,
      endDate: defaults.endDate,
    });
    setFetchInput({
      ...fetchInput,
      startDate: defaults.f_startDate,
      endDate: defaults.f_endDate,
    });
    setSearchText("");
    setPage(0);
  };

  // Sort dealership data alphabetically by dealership_name
  const sortedDealershipData = [...dealershipData].sort((a, b) => {
    const nameA = a.dealership_name?.toLowerCase() || '';
    const nameB = b.dealership_name?.toLowerCase() || '';
    return nameA.localeCompare(nameB);
  });

  const filteredDealershipData = sortedDealershipData.filter((dealer) => {
    if (!searchText.trim()) return true;
    return (dealer.dealership_name || "")
      .toLowerCase()
      .includes(searchText.trim().toLowerCase());
  });

  const totalRecords = filteredDealershipData.length;
  const startIdx = page * rowsPerPage;
  const pagedDealers = filteredDealershipData.slice(
    startIdx,
    startIdx + rowsPerPage,
  );
  const startRecord = totalRecords > 0 ? startIdx + 1 : 0;
  const endRecord = Math.min(startIdx + rowsPerPage, totalRecords);

  // CSV export: all filtered rows (not just the current page). Each "(+n)"
  // follow-up count gets its own numeric column so it can be summed/sorted.
  const handleExport = () => {
    const headers = [
      "Dealer",
      "Insurance Verified",
      "Insurance Verified Follow-up",
      ...(checkService?.knowledgeHub ? ["Knowledge Hub Run"] : []),
      "ID Verification",
      "Prequals Run",
      "Quotes Requested",
      "Reviews Provided",
      "Reviews Follow-up",
      "Satisfied Yes",
      "Satisfied Yes Follow-up",
      "Satisfied No",
      "Satisfied No Follow-up",
      "1st Service Set",
      "1st Service Set Follow-up",
      "Referrals Provided",
      "Referrals Follow-up",
      "Apps Downloaded",
    ];

    const rows = filteredDealershipData.map((d) => [
      d.dealership_name || "",
      d.completed || 0,
      d.verified_followup || 0,
      ...(checkService?.knowledgeHub ? [d.watchedArtifacs || 0] : []),
      "N/A",
      "N/A",
      d.quotesRequested || 0,
      d.review_provided || 0,
      d.review_followup || 0,
      d.satisfied_yes || 0,
      d.satisfied_yes_followup || 0,
      d.satisfied_no || 0,
      d.satisfied_no_followup || 0,
      d.appointment_set || 0,
      d.appointment_followup || 0,
      d.referral_provided || 0,
      d.referral_followup || 0,
      d.apps_downloaded || 0,
    ]);

    const csv = [
      ["Group Dealership Report"],
      [`Date Range: ${filterData?.startDate} - ${filterData?.endDate}`],
      [],
      headers,
      ...rows,
    ]
      .map((row) => row.map(escapeCsv).join(","))
      .join("\r\n");

    // BOM so Excel reads UTF-8 correctly
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Dealership_Report_${moment().format("YYYY-MM-DD")}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Paper
      sx={{
        ...ManagerStyle.wrapper,
        display: "flex",
        flexDirection: "column",
        height: "440px",
      }}
    >
      <Box
        sx={{
          display: { xs: "block", md: "flex" },
          padding: "10px",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6" fontWeight="bold">
          Group's List of Dealer
        </Typography>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "16px",
            marginTop: "10px",
          }}
        >
          <TextField
            size="small"
            placeholder="Search dealer"
            value={searchText}
            onChange={(event) => {
              setSearchText(event.target.value);
              setPage(0);
            }}
            sx={{
              ...CustomerStyle.textField,
              width: "180px",
              "& .MuiInputBase-root": { height: "40px", fontSize: "13px" },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: "#919EAB" }} />
                </InputAdornment>
              ),
              endAdornment: searchText ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearchText("");
                      setPage(0);
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Start Date"
              value={filterData.startDate}
              maxDate={new Date()}
              onChange={(event) => {
                setFilterData({ ...filterData, startDate: moment(event).format("MM/DD/YYYY") });
                setFetchInput({ ...fetchInput, startDate: moment(event).format("YYYY-MM-DD") });
                setPage(0);
              }}
              renderInput={(params) => (
                <TextField
                  size="small"
                  sx={CustomerStyle.textField}
                  {...params}
                />
              )}
              components={{
                OpenPickerIcon: () => (
                  <InputAdornment position="end">
                    <IconButton edge="end">
                      <EventIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </LocalizationProvider>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="End Date"
              value={filterData.endDate}
              minDate={new Date(filterData.startDate)}
              maxDate={new Date()}
              onChange={(event) => {
                setFilterData({ ...filterData, endDate: moment(event).format("MM/DD/YYYY") });
                setFetchInput({ ...fetchInput, endDate: moment(event).format("YYYY-MM-DD") });
                setPage(0);
              }}
              renderInput={(params) => (
                <TextField
                  size="small"
                  sx={CustomerStyle.textField}
                  {...params}
                />
              )}
              components={{
                OpenPickerIcon: () => (
                  <InputAdornment position="end">
                    <IconButton edge="end">
                      <EventIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </LocalizationProvider>

          <Button
            onClick={handleReset}
            startIcon={<RestartAltIcon sx={{ fontSize: 16 }} />}
            variant="outlined"
            size="small"
            sx={{
              height: "36px",
              textTransform: "none",
              fontSize: "13px",
              fontWeight: 700,
              borderColor: "#BDBDBD",
              color: "#616161",
              whiteSpace: "nowrap",
              px: "12px",
            }}
          >
            Reset
          </Button>

          <Button
            onClick={handleExport}
            startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />}
            variant="outlined"
            size="small"
            sx={{
              height: "36px",
              textTransform: "none",
              fontSize: "13px",
              fontWeight: 700,
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.main,
              whiteSpace: "nowrap",
              px: "12px",
              "&:hover": {
                backgroundColor: "rgba(0,171,85,0.08)",
                borderColor: theme.palette.primary.main,
              },
            }}
          >
            Export
          </Button>
        </div>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          px: "12px",
          "&::-webkit-scrollbar": {
            display: "none",
          },
        }}
      >
        <Table stickyHeader>
          <TableHead sx={ManagerStyle.tableHead}>
            <TableRow sx={ManagerStyle.tableRow}>
              <TableCell sx={ManagerStyle.tableCell} width="15%">
                <strong>Dealers</strong>
              </TableCell>
              <TableCell sx={ManagerStyle.tableCell} align="center">
                <strong>Completed</strong>
              </TableCell>
              {checkService?.knowledgeHub && (
                <TableCell sx={ManagerStyle.tableCell} align="center">
                  <strong>Knowledge Hub Run</strong>
                </TableCell>
              )}
              <TableCell sx={ManagerStyle.tableCell} align="center">
                <strong>ID Verification</strong>
              </TableCell>
              <TableCell sx={ManagerStyle.tableCell} align="center">
                <strong>Prequals Run</strong>
              </TableCell>
              <TableCell sx={ManagerStyle.tableCell} align="center">
                <strong>Quotes Requested</strong>
              </TableCell>
              <TableCell sx={ManagerStyle.tableCell} align="center">
                <strong>Review</strong>
              </TableCell>
              <TableCell sx={ManagerStyle.tableCell} align="center">
                <strong>Satisfied (Y/N)</strong>
              </TableCell>
              <TableCell sx={ManagerStyle.tableCell} align="center">
                <strong>Appointment</strong>
              </TableCell>
              <TableCell sx={ManagerStyle.tableCell} align="center">
                <strong>Referral</strong>
              </TableCell>
              <TableCell sx={ManagerStyle.tableCell} align="center">
                <strong>Apps</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell sx={ManagerStyle.noBorder} colSpan={12} align="center">
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <CircularProgress />
                  </Box>
                </TableCell>
              </TableRow>
            ) : pagedDealers.length > 0 ? (
              pagedDealers.map((dealer) => (
                <TableRow
                  key={dealer.dealership_id}
                  onClick={() => handleDealershipClick(dealer)}
                  sx={{
                    "&:hover": {
                      backgroundColor: "rgba(0,171,85,0.05)",
                      cursor: "pointer",
                      "& > *": {
                        color: theme.palette.primary.main,
                      },
                    },
                  }}
                >
                  <TableCell
                    sx={{
                      ...ManagerStyle.noBorder,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <GroupIcon
                      sx={{
                        fontSize: 20,
                        color: theme.palette.primary.main,
                      }}
                    />
                    {dealer.dealership_name}
                  </TableCell>
                  {/* Completed → Insurance Verified "(+value)" = verification_repull reruns */}
                  <TableCell align="center" sx={ManagerStyle.noBorder}>
                    {dealer.completed || 0}
                    <FollowUpBadge
                      value={dealer?.verified_followup}
                      color={theme.palette.primary.main}
                    />
                  </TableCell>
                  {checkService?.knowledgeHub && (
                    <TableCell align="center" sx={ManagerStyle.noBorder}>
                      {dealer.watchedArtifacs || 0}
                    </TableCell>
                  )}
                  <TableCell align="center" sx={ManagerStyle.noBorder}>
                    N/A
                  </TableCell>
                  <TableCell align="center" sx={ManagerStyle.noBorder}>
                    N/A
                  </TableCell>
                  <TableCell align="center" sx={ManagerStyle.noBorder}>
                    {dealer.quotesRequested || 0}
                  </TableCell>
                  {/* Review → Reviews Provided = isReview rows + is_followup_review rows.
                      "(+value)" = is_followup_review only (already included in the total). */}
                  <TableCell align="center" sx={ManagerStyle.noBorder}>
                    {dealer.review_provided || 0}
                    <FollowUpBadge
                      value={dealer?.review_followup}
                      color={theme.palette.primary.main}
                    />
                  </TableCell>

                  {/* Satisfied (Y/N) → Direct Yes/No | Follow-up Yes/No.
                      The "No" value in both the direct and follow-up pair is
                      shown in the theme's error (red) color. */}
                  <TableCell align="center" sx={ManagerStyle.noBorder}>
                    {dealer.satisfied_yes || 0}
                    {"/"}
                    <Box component="span" sx={{ color: theme.palette.error.main }}>
                      {dealer.satisfied_no || 0}
                    </Box>
                    {" | "}
                    {dealer.satisfied_yes_followup || 0}
                    {"/"}
                    <Box component="span" sx={{ color: theme.palette.error.main }}>
                      {dealer.satisfied_no_followup || 0}
                    </Box>
                  </TableCell>
                  {/* Appointment → 1st Service Set "(+value)" = follow-up scheduling link (service_maintenance_vehicle) */}
                  <TableCell align="center" sx={ManagerStyle.noBorder}>
                    {dealer.appointment_set || 0}
                    <FollowUpBadge
                      value={dealer?.appointment_followup}
                      color={theme.palette.primary.main}
                    />
                  </TableCell>
                  {/* Referral → Referrals Provided "(+value)" = follow-up referral link (is_followup) */}
                  <TableCell align="center" sx={ManagerStyle.noBorder}>
                    {dealer.referral_provided || 0}
                    <FollowUpBadge
                      value={dealer?.referral_followup}
                      color={theme.palette.primary.main}
                    />
                  </TableCell>
                  <TableCell align="center" sx={ManagerStyle.noBorder}>
                    {dealer.apps_downloaded || 0}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell sx={ManagerStyle.noBorder} colSpan={12} align="center">
                  <Typography variant="body2" color="textSecondary">
                    {searchText
                      ? "No dealers found"
                      : "No dealership data available"}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>

      {/* Footer / Pagination Bar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 16px",
          borderTop: "1px solid #F4F6F8",
          mt: "auto",
          pt: "4px",
          flexWrap: "wrap",
          gap: "8px",
        }}
      >
        <Typography sx={{ fontSize: "12px", color: "#637381" }}>
          Showing {startRecord} to {endRecord} of {totalRecords} dealers
        </Typography>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={totalRecords}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(event, newPage) => {
            setPage(newPage);
          }}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          labelDisplayedRows={() => ""}
        />
      </Box>
    </Paper>
  );
};

export default DealershipTableComponent;